import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import {kit, radius} from './tokens';

export interface StripPhoto {
  url: string;
  caption?: string;
}

interface PhotoStripProps {
  photos: StripPhoto[];
  onPhotoClick?: (index: number) => void;
  sx?: SxProps<Theme>;
}

// Rounded photo thumbnails with caption pills -- web port of `.photostrip` /
// `.ph` in web-detail-1-quotes.html. Real project photos vary in count (not
// always a tidy multiple of 4 like the mockup's curated 4), so rather than
// the approved rulebook's fixed 2-up grid on phone, this scrolls
// horizontally at that size -- every photo stays reachable and full height
// regardless of count. Renders nothing when there are no photos (never a
// blank strip).
const PhotoStrip = ({photos, onPhotoClick, sx}: PhotoStripProps) => {
  if (photos.length === 0) {
    return null;
  }

  return (
    <Box
      sx={[
        (theme) => ({
          display: 'flex',
          gap: '14px',
          overflowX: 'auto',
          [theme.breakpoints.up('sm')]: {
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            overflowX: 'visible',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      {photos.map((photo, index) => (
        <Box
          key={photo.url}
          onClick={onPhotoClick ? () => onPhotoClick(index) : undefined}
          sx={{
            position: 'relative',
            height: 124,
            width: {xs: 160, sm: 'auto'},
            flexShrink: 0,
            borderRadius: `${radius.md}px`,
            border: `1px solid ${kit.line}`,
            overflow: 'hidden',
            cursor: onPhotoClick ? 'pointer' : 'default',
          }}>
          <Box
            component="img"
            src={photo.url}
            alt={photo.caption ?? ''}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {photo.caption ? (
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                bottom: 8,
                maxWidth: 'calc(100% - 16px)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                backgroundColor: 'rgba(255,255,255,.92)',
                borderRadius: `${radius.pill}px`,
                padding: '3px 9px',
                fontSize: 10.5,
                fontWeight: 600,
                color: kit.inkSoft,
              }}>
              {photo.caption}
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

export default PhotoStrip;
